// v1.1.0 · Contacto: formulario guiado (tipo de proyecto y plazo) + WhatsApp, correo y oficina.
// v1.2.0 · Formulario en dos pasos (primero qué necesita, luego sus datos).
// v1.6.0 · «¿Qué necesitas?» admite varias opciones y «Otra» con campo para describirla.
//           Usa el mismo endpoint público POST /api/contact; tipo, plazo y empresa viajan dentro del mensaje.
// v1.20.1 · preguntas de texto y número además de las de lista.
// v1.19.0 · también viajan como datos (empresa, plazo, necesidad y las preguntas «En formularios» del CRM) con los utm.
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { MessageCircle, Mail, MapPin, Loader2, CheckCircle2 } from 'lucide-react';
import { contactData, projectTypes, timelines } from '../../data/site';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY = { name: '', company: '', email: '', phone: '', types: [], other: '', timeline: '', message: '' };
const OTHER = 'Otra';
const SKIP = ['cuando_quiere_empezar', 'necesidad_principal'];   // ya se preguntan en el paso 1
const UTM = (() => { try { const q = new URLSearchParams(window.location.search); return Object.fromEntries(['source', 'medium', 'campaign', 'content'].map((k) => [k, q.get(`utm_${k}`) || '']).filter(([, v]) => v)); } catch { return {}; } })();

function MultiChoice({ options, values, onToggle, legend, hint }) {
  return (
    <fieldset>
      <legend className="mb-2.5 text-[15px] font-semibold">{legend} <span className="font-normal text-[var(--ink-2)]">{hint}</span></legend>
      <div className="flex flex-wrap gap-2">
        {options.map((o) => {
          const on = values.includes(o);
          return (
            <label key={o}
                   className={`inline-flex cursor-pointer items-center gap-1.5 rounded-full border px-3.5 py-2 text-[14px] font-medium transition-[background-color,border-color,transform] duration-150 active:scale-[.97] has-[:focus-visible]:outline has-[:focus-visible]:outline-[3px] has-[:focus-visible]:outline-[var(--sky)] ${
                     on ? 'border-[var(--ink)] bg-[var(--ink)] text-white' : 'border-[var(--line)] bg-white hover:border-[var(--brand)]'
                   }`}>
              <input type="checkbox" value={o} checked={on} onChange={() => onToggle(o)} className="sr-only" />
              <span aria-hidden className={`flex h-4 w-4 items-center justify-center rounded-[4px] border text-[11px] leading-none ${on ? 'border-white/70 bg-white/15' : 'border-[var(--line)]'}`}>{on ? '✓' : ''}</span>
              {o}
            </label>
          );
        })}
      </div>
    </fieldset>
  );
}

function Choice({ name, options, value, onChange, legend }) {
  return (
    <fieldset>
      <legend className="mb-2.5 text-[15px] font-semibold">{legend}</legend>
      <div className="flex flex-wrap gap-2">
        {options.map((o) => (
          <label key={o}
                 className={`cursor-pointer rounded-full border px-3.5 py-2 text-[14px] font-medium transition-colors has-[:focus-visible]:outline has-[:focus-visible]:outline-[3px] has-[:focus-visible]:outline-[var(--sky)] ${
                   value === o ? 'border-[var(--ink)] bg-[var(--ink)] text-white' : 'border-[var(--line)] bg-white hover:border-[var(--brand)]'
                 }`}>
            <input type="radio" name={name} value={o} checked={value === o} onChange={() => onChange(o)} className="sr-only" />
            {o}
          </label>
        ))}
      </div>
    </fieldset>
  );
}

const field = 'mt-1.5 w-full rounded-[10px] border border-[var(--line)] bg-white px-3.5 py-3 text-[16px] placeholder:text-[#9AA1BA] focus:border-[var(--brand)]';

export default function SiteContact() {
  const [f, setF] = useState(EMPTY);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [step, setStep] = useState(1);
  const [questions, setQuestions] = useState([]);   // v1.19.0
  const [extra, setExtra] = useState({});
  useEffect(() => { axios.get(`${API}/public/lead-fields`).then((r) => setQuestions((r.data.items || []).filter((q) => !SKIP.includes(q.key) && ['select', 'text', 'number'].includes(q.type)))).catch(() => setQuestions([])); }, []);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target ? e.target.value : e }));
  const toggleType = (v) => setF((p) => ({ ...p, types: p.types.includes(v) ? p.types.filter((x) => x !== v) : [...p.types, v] }));
  const hasOther = f.types.includes(OTHER);
  const typeList = f.types.map((x) => (x === OTHER ? `Otra: ${f.other.trim() || 'sin detalle'}` : x));
  const canContinue = f.types.length > 0 && (!hasOther || f.other.trim().length >= 3);

  const submit = async (e) => {
    e.preventDefault();
    if (f.message.trim().length < 10) {
      toast.error('Cuéntanos un poco más sobre el proyecto (al menos 10 caracteres).');
      return;
    }
    const lines = [
      typeList.length && `Tipo de proyecto: ${typeList.join(', ')}`,
      f.timeline && `Plazo: ${f.timeline}`,
      f.company && `Empresa: ${f.company}`,
    ].filter(Boolean);
    const message = (lines.length ? lines.join('\n') + '\n\n' : '') + f.message.trim();
    setSending(true);
    try {
      await axios.post(`${API}/contact`, {
        name: f.name.trim(), email: f.email.trim(), phone: f.phone.trim() || null, message: message.slice(0, 2000),
        company: f.company.trim() || null, utm: UTM,
        lead: { ...Object.fromEntries(Object.entries(extra).filter(([, v]) => v)), cuando_quiere_empezar: f.timeline || undefined, necesidad_principal: f.types.filter((x) => x !== OTHER) },
      });
      setExtra({});
      setSent(true);
      setF(EMPTY);
      setStep(1);
    } catch (err) {
      const st = err?.response?.status;
      toast.error(st === 422
        ? 'Revisa el correo y el teléfono (máximo 20 caracteres) e inténtalo de nuevo.'
        : 'No se pudo enviar la solicitud. Escríbenos por WhatsApp y te atendemos de inmediato.');
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contacto" data-scan className="seam scroll-mt-24 py-20 lg:py-28">
      <div className="mx-auto grid max-w-[1240px] gap-14 px-5 sm:px-8 lg:grid-cols-[0.9fr_1.3fr] lg:gap-20">
        <div data-reveal>
          <h2 className="display text-[2.8rem] sm:text-[3.6rem]">Cuéntanos qué proceso quieres convertir en sistema</h2>
          <p className="mt-5 text-[1.0625rem] leading-[1.65] text-[var(--ink-2)]">
            Revisamos tu caso y te proponemos alcance, plazo y costo. Sin compromiso.
          </p>

          <ul className="mt-10 space-y-6 text-[16px]">
            <li>
              <a href={contactData.whatsappLink} target="_blank" rel="noopener noreferrer" className="group flex gap-4">
                <MessageCircle size={22} className="mt-0.5 shrink-0 text-[#25A244]" aria-hidden />
                <span>
                  <span className="block font-semibold group-hover:underline">WhatsApp {contactData.whatsapp}</span>
                  <span className="text-[15px] text-[var(--ink-2)]">Te respondemos en minutos.</span>
                </span>
              </a>
            </li>
            <li>
              <a href={`mailto:${contactData.email}`} className="group flex gap-4">
                <Mail size={22} className="mt-0.5 shrink-0 text-[var(--brand)]" aria-hidden />
                <span className="block font-semibold group-hover:underline">{contactData.email}</span>
              </a>
            </li>
            <li className="flex gap-4">
              <MapPin size={22} className="mt-0.5 shrink-0 text-[var(--brand)]" aria-hidden />
              <span>
                <span className="block font-semibold">Oficina en Quito</span>
                <span className="text-[15px] text-[var(--ink-2)]">{contactData.address}</span>
              </span>
            </li>
          </ul>
        </div>

        <div data-reveal style={{ '--rd': '140ms' }} className="rounded-[10px] border border-[var(--line)] bg-[var(--sheet)] p-6 shadow-[0_30px_60px_-45px_rgba(15,42,58,.5)] sm:p-9">
          {sent ? (
            <div className="flex min-h-[360px] flex-col items-start justify-center" role="status">
              <CheckCircle2 size={36} className="text-[var(--leaf)]" aria-hidden />
              <h3 className="mt-4 text-[1.6rem] font-extrabold">Recibimos tu solicitud</h3>
              <p className="mt-2 max-w-[44ch] text-[16px] leading-[1.6] text-[var(--ink-2)]">
                Un especialista de Analytica revisará tu caso y te contactará. Si es urgente, escríbenos por WhatsApp.
              </p>
              <button onClick={() => setSent(false)} className="btn-quiet mt-6">Enviar otra solicitud</button>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-7" data-testid="contact-form" noValidate={step === 1}>
              {/* v1.2.0 · Dos pasos: primero qué necesita (un clic), luego sus datos */}
              <p className="text-[14px] font-semibold text-[var(--ink-2)]" aria-live="polite">Paso {step} de 2</p>
              {step === 1 ? (
                <>
                  <MultiChoice legend="¿Qué necesitas?" hint="Puedes elegir varias." options={projectTypes} values={f.types} onToggle={toggleType} />
                  {hasOther && (
                    <label className="rise block text-[15px] font-semibold" style={{ '--d': '0ms' }}>¿Cuál?
                      <input className={field} name="other" value={f.other} onChange={set('other')} maxLength={120} autoFocus
                             placeholder="Por ejemplo: firma electrónica de contratos" data-testid="contact-other" />
                    </label>
                  )}
                  <Choice legend="¿Para cuándo?" name="timeline" options={timelines} value={f.timeline} onChange={set('timeline')} />
                  <button type="button" onClick={() => setStep(2)} disabled={!canContinue}
                          className="btn-go w-full justify-center text-[16px] disabled:cursor-not-allowed disabled:opacity-50" data-testid="contact-next">
                    Continuar
                  </button>
                  {!canContinue && <p className="text-[13px] text-[var(--ink-2)]">{hasOther ? 'Cuéntanos en pocas palabras qué otra cosa necesitas.' : 'Elige al menos una opción para continuar.'}</p>}
                </>
              ) : (
                <>
                  <div className="flex flex-wrap items-center gap-2 text-[14px]">
                    {typeList.map((x) => <span key={x} className="rounded-full bg-[var(--ink)] px-3 py-1 font-medium text-white">{x}</span>)}
                    {f.timeline && <span className="rounded-full border border-[var(--line)] bg-white px-3 py-1">{f.timeline}</span>}
                    <button type="button" onClick={() => setStep(1)} className="ml-1 font-semibold text-[var(--brand)] underline underline-offset-4">Cambiar</button>
                  </div>
                  <div className="grid gap-5 sm:grid-cols-2">
                    <label className="block text-[15px] font-semibold">Nombre
                      <input className={field} name="name" value={f.name} onChange={set('name')} required minLength={2} maxLength={100} autoComplete="name" />
                    </label>
                    <label className="block text-[15px] font-semibold">Empresa
                      <input className={field} name="company" value={f.company} onChange={set('company')} maxLength={120} autoComplete="organization" />
                    </label>
                    <label className="block text-[15px] font-semibold">Correo
                      <input className={field} type="email" name="email" value={f.email} onChange={set('email')} required autoComplete="email" />
                    </label>
                    <label className="block text-[15px] font-semibold">Teléfono o WhatsApp
                      <input className={field} type="tel" name="phone" value={f.phone} onChange={set('phone')} maxLength={20} autoComplete="tel" placeholder="+593" />
                    </label>
                  </div>
                  {questions.length > 0 && (
                    <div className="grid gap-5 sm:grid-cols-2" data-testid="contact-lead">
                      {questions.map((q) => (
                        <label key={q.key} className="block text-[15px] font-semibold">{q.name} <span className="font-normal text-[var(--ink-2)]">(opcional)</span>
                          {q.type === 'select' ? (
                            <select className={field} value={extra[q.key] || ''} onChange={(e) => setExtra((p) => ({ ...p, [q.key]: e.target.value }))}>
                              <option value="">Elegir…</option>{q.options.map((o) => <option key={o} value={o}>{o}</option>)}
                            </select>
                          ) : (   /* v1.20.1 · también los campos de texto y número (p. ej. Cargo) */
                            <input className={field} type={q.type === 'number' ? 'number' : 'text'} inputMode={q.type === 'number' ? 'numeric' : undefined} maxLength={120}
                                   value={extra[q.key] || ''} onChange={(e) => setExtra((p) => ({ ...p, [q.key]: e.target.value }))} />
                          )}
                        </label>
                      ))}
                    </div>
                  )}
                  <label className="block text-[15px] font-semibold">¿Qué proceso quieres mejorar?
                    <textarea className={`${field} min-h-[120px] resize-y`} name="message" value={f.message} onChange={set('message')} required maxLength={1700}
                              placeholder="Por ejemplo: coordinamos 300 entregas por semana con Excel y WhatsApp, y perdemos el rastro de los pedidos." />
                  </label>
                  <button type="submit" disabled={sending} className="btn-go w-full justify-center text-[16px] disabled:opacity-70" data-testid="contact-submit">
                    {sending ? <><Loader2 size={18} className="animate-spin" aria-hidden /> Enviando solicitud</> : 'Solicitar propuesta'}
                  </button>
                  <p className="text-[13px] text-[var(--ink-2)]">Usamos tus datos solo para responder a esta solicitud.</p>
                </>
              )}
            </form>
          )}
        </div>
      </div>
    </section>
  );
}
